import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { UI } from "@/data/site";
import { useLang } from "@/context/LanguageContext";

const Header = () => {
  const { lang, toggle, t, lp } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/marketing-comunicacion-eventos", label: UI.nav.marketing, accent: "hover:text-brand-green" },
    { to: "/produccion-audiovisual", label: UI.nav.audiovisual, accent: "hover:text-brand-gold" },
    { to: "/proyectos", label: UI.nav.proyectos, accent: "hover:text-white" },
    { to: "/actualidad", label: UI.nav.actualidad, accent: "hover:text-white" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "py-5 bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <span
            data-testid="since-badge"
            className="hidden sm:inline-flex items-center font-display text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white/50 border-l border-white/20 pl-3 leading-tight"
          >
            {t({ es: "Desde 2007", en: "Since 2007" })}
          </span>
        </div>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={lp(l.to)}
              data-testid={`nav-${l.to.replace(/\//g, "")}`}
              className={`font-display text-[0.8rem] font-semibold uppercase tracking-[0.14em] text-white/70 transition-colors ${l.accent}`}
            >
              {t(l.label)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            data-testid="language-toggle"
            className="hidden sm:flex items-center font-display text-xs font-bold tracking-widest text-white/70 hover:text-white transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "es" ? "text-brand-gold" : ""}>ES</span>
            <span className="mx-1 text-white/30">/</span>
            <span className={lang === "en" ? "text-brand-gold" : ""}>EN</span>
          </button>

          <Link
            to={lp("/contacto")}
            data-testid="header-contact-cta"
            className="hidden sm:inline-flex items-center rounded-full bg-white text-ink px-5 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] hover:bg-brand-gold transition-colors"
          >
            {t(UI.cta.talk)}
          </Link>

          <button
            className="lg:hidden text-white"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass mt-3 mx-4 rounded-2xl p-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={lp(l.to)}
                className="font-display text-lg font-bold text-white"
              >
                {t(l.label)}
              </Link>
            ))}
            <Link to={lp("/contacto")} className="font-display text-lg font-bold text-brand-gold">
              {t(UI.nav.contacto)}
            </Link>
            <button
              onClick={toggle}
              className="text-left font-display text-sm font-bold tracking-widest text-white/60"
            >
              {lang === "es" ? "ES / en" : "es / EN"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
